import {Component, EventEmitter, Inject, Input, OnInit, Output} from '@angular/core';
import {FormArray, FormControl, FormGroup} from '@angular/forms';
import {dateToString} from '@emma-labs/shared/angular';
import {ENVIRONMENT, EnvironmentBroker, removeNullProperties} from '@emma-labs/shared/typescript';

@Component({
  template: '',
})
export class BaseSectionComponent<
  F extends FormGroup | FormArray<FormGroup> | FormControl,
  P extends {[K in keyof P]: unknown} | {[K in keyof P]: unknown}[],
  O extends {[K in keyof O]: unknown} | {[K in keyof O]: unknown}[] = P,
> implements OnInit
{
  form!: F;
  isReadonly = false;
  isCompleted = false;
  isDev = false;
  isPropsReceived = false;
  isButtonLoading = false;
  isPendingUpdate = false;

  @Input() set readonly(isReadonly: boolean) {
    this.isReadonly = isReadonly;

    if (isReadonly) {
      this.form.disable({emitEvent: false});
    }
  }

  @Input() set completed(isCompleted: boolean) {
    this.isCompleted = isCompleted;
  }

  @Input({required: true})
  set props(props: P | undefined) {
    if (!this.isPropsReceived && props != null) {
      this.isPropsReceived = true;

      if (this.form instanceof FormGroup && !Array.isArray(props)) {
        this.updateFormGroup(this.form, props);
        return;
      }

      if (this.form instanceof FormArray && Array.isArray(props)) {
        this.form.controls.forEach((form, index) => {
          this.updateFormGroup(form, props[index]);
        });
        return;
      }

      if (this.form instanceof FormControl) {
        this.form.patchValue(props, {emitEvent: false});
        return;
      }

      throw new Error('The form type expects props that match.');
    }
  }

  @Output() propsChange = new EventEmitter<{output: O; callback?: (status: boolean) => void}>();

  constructor(@Inject(ENVIRONMENT) environment: EnvironmentBroker) {
    this.isDev = environment.showDevControls;
  }

  ngOnInit(): void {
    if (!this.form) {
      throw new Error('Form not created.');
    }
  }

  formToOutput(): O {
    if (this.form instanceof FormArray) {
      const output: FormGroup[] = [];
      this.form.controls.forEach((control) => output.push(control.getRawValue()));
      return output as unknown as O;
    }

    return {...removeNullProperties(this.form.getRawValue())} as unknown as O;
  }

  private processProps(props: {[key: string]: unknown}) {
    const processedProps = {};
    Object.entries(props).forEach(([k, v]) => {
      let processedValue = v;
      if (typeof v === 'object' && v != null) {
        if (v instanceof Date) {
          processedValue = dateToString(v, true);
        } else {
          processedValue = this.processProps(v as {[key: string]: unknown});
        }
      }
      processedProps[k] = processedValue;
    });
    return processedProps;
  }

  private updateFormGroup(formGroup: FormGroup, props: {[K in keyof P]: unknown}) {
    const propsWithoutArrays: {[K in keyof P]: unknown} = this.getPropsWithoutArrays(props, formGroup);
    formGroup.patchValue(this.processProps(propsWithoutArrays), {emitEvent: false});

    Object.keys(props).forEach((key) => {
      const form = formGroup.get(key);
      if (form instanceof FormArray && Array.isArray(props[key])) {
        form.patchValue(
          props[key].map((p) => this.processProps(p)),
          {emitEvent: false},
        );
      }
    });
  }

  private getPropsWithoutArrays(props: {[K in keyof P]: unknown}, formGroup: FormGroup): P {
    return Object.keys(props).reduce((acc, key) => {
      if (!(formGroup.get(key) instanceof FormArray)) {
        acc[key] = props[key];
      }
      return acc;
    }, {} as P);
  }
}
